import type { FetchData } from '@hooks/useDataFetcher';
import { useCallback, useMemo, useRef, useState } from 'react';
import type {
  IAssortmentCarConfigVariant,
  IAssortmentCarPlan,
  IAssortmentCarPlanFilters,
  IAssortmentCarProduct,
  IAssortmentClassification,
  IAssortmentPackage,
  IAssortmentPackageVariant,
  IAssortmentProduct,
  IAssortmentProductGroup,
  IAssortmentProductVariant,
  IAssortmentSkuCategory,
  IAssortmentSpecification,
} from 'types/custom';

import type { SnackbarActions } from './useSnackbar';

interface IState {
  products: Array<IAssortmentProduct>;
  skuCategories: Array<IAssortmentSkuCategory>;
  productGroups: Array<IAssortmentProductGroup>;
  specifications: Array<IAssortmentSpecification>;
  classifications: Array<IAssortmentClassification>;
  productVariants: Array<IAssortmentProductVariant | IAssortmentPackageVariant>;
}

interface IActions {
  getSkuCategories: () => Promise<IAssortmentSkuCategory[]>;
  getProductGroups: () => Promise<IAssortmentProductGroup[]>;
  getAvailableCars: () => Promise<IAssortmentCarConfigVariant[]>;
  getProducts: (
    pgCodes?: string[],
  ) => Promise<Array<IAssortmentPackage | IAssortmentProduct>>;
  createCarPlan: (car: IAssortmentCarConfigVariant) => Promise<any | undefined>;
  applyCarPlan: (planId: string) => Promise<boolean>;
  createProductGroup: (
    body: IAssortmentProductGroup,
  ) => Promise<IAssortmentProductGroup | undefined>;
  updateProductGroup: (
    id: string,
    body: IAssortmentProductGroup,
  ) => Promise<IAssortmentProductGroup | undefined>;
  getCarPlans: (
    limit: number,
    offset: number,
    status?: 'WaitingApproval' | 'Active',
    modelYear?: number,
    engineType?: string,
    classification?: string,
    search?: string,
  ) => Promise<
    { data: IAssortmentCarPlan[]; metadata: { totalCount: number } } | undefined
  >;
  getCarPlanFilters: () => Promise<IAssortmentCarPlanFilters | undefined>;
  deleteProductGroup: (id: string) => Promise<void>;
  getSpecifications: () => Promise<IAssortmentSpecification[]>;
  getClassifications: () => Promise<IAssortmentClassification[]>;
  getCarProducts: (market: string) => Promise<IAssortmentCarProduct[]>;
  createSpecification: (
    body: IAssortmentSpecification,
  ) => Promise<IAssortmentSpecification | undefined>;
  updateSpecification: (
    id: string,
    body: IAssortmentSpecification,
  ) => Promise<IAssortmentSpecification | undefined>;
  deleteSpecification: (id: string) => Promise<void>;
  createClassification: (
    body: IAssortmentClassification,
  ) => Promise<IAssortmentClassification | undefined>;
  updateClassification: (
    id: string,
    body: IAssortmentClassification,
  ) => Promise<IAssortmentClassification | undefined>;
  deleteClassification: (id: string) => Promise<void>;
  getProductVariants: () => Promise<
    (IAssortmentPackageVariant | IAssortmentProductVariant)[]
  >;
  createProduct: (
    product: IAssortmentPackage | IAssortmentProduct,
  ) => Promise<IAssortmentPackage | IAssortmentProduct | undefined>;
  updateProduct: (
    id: string,
    product: IAssortmentPackage | IAssortmentProduct,
  ) => Promise<IAssortmentPackage | IAssortmentProduct | undefined>;
  getProductById: (
    id: string,
    notify?: boolean,
  ) => Promise<IAssortmentPackage | IAssortmentProduct | undefined>;
}

export type UseAssortment = (
  fetchData: FetchData,
  snackbar: SnackbarActions,
) => [IState & { loading: boolean }, IActions];

export const useAssortment: UseAssortment = (makeRequest, snackbar) => {
  const [pendingActions, setPendingActions] = useState<Set<string>>(new Set());
  const inFlightRequests = useRef<Map<string, Promise<unknown>>>(new Map());
  const [state, setState] = useState<IState>({
    products: [],
    productGroups: [],
    skuCategories: [],
    specifications: [],
    classifications: [],
    productVariants: [],
  });
  const stateRef = useRef<IState>(state);
  stateRef.current = state;

  const startLoading = useCallback((actionKey: string) => {
    setPendingActions((prev) => {
      if (prev.has(actionKey)) {
        return prev;
      }

      return new Set(prev).add(actionKey);
    });
  }, []);

  const stopLoading = useCallback(
    (actionKey: string) =>
      setPendingActions((prev) => {
        if (!prev.has(actionKey)) {
          return prev;
        }

        const newQueue = new Set(prev);
        newQueue.delete(actionKey);
        return newQueue;
      }),
    [],
  );

  const runWithDedup = useCallback(
    async <T>(args: {
      actionKey: string;
      request: () => Promise<T>;
    }): Promise<T> => {
      const existingRequest = inFlightRequests.current.get(args.actionKey) as
        | Promise<T>
        | undefined;

      if (existingRequest) {
        return existingRequest;
      }

      startLoading(args.actionKey);

      const requestPromise = args.request().finally(() => {
        inFlightRequests.current.delete(args.actionKey);
        stopLoading(args.actionKey);
      }) as Promise<T>;

      inFlightRequests.current.set(args.actionKey, requestPromise);

      return requestPromise;
    },
    [startLoading, stopLoading],
  );

  // Car products and plans

  const getCarProducts = useCallback<IActions['getCarProducts']>(
    async (market) => {
      try {
        return await runWithDedup({
          actionKey: `getCarProducts:${market}`,
          request: async () => {
            const carProducts = await makeRequest<IAssortmentCarProduct[]>(
              `/api/assortment/car-products?market=${encodeURIComponent(market)}`,
            );

            if (!Array.isArray(carProducts)) {
              throw new Error('An error occurred while fetching car products.');
            }

            return carProducts;
          },
        });
      } catch (err: any) {
        snackbar.error(err.message);
        return [];
      }
    },
    [makeRequest, runWithDedup, snackbar],
  );

  const getAvailableCars = useCallback<
    IActions['getAvailableCars']
  >(async () => {
    try {
      return await runWithDedup({
        actionKey: 'getAvailableCars',
        request: async () => {
          const availableCars = await makeRequest<
            IAssortmentCarConfigVariant[]
          >('/api/assortment/plans/car/available-for-creation');

          if (!Array.isArray(availableCars)) {
            throw new Error('An error occurred while fetching available cars.');
          }

          return availableCars;
        },
      });
    } catch (err: any) {
      snackbar.error(err.message);
      return [];
    }
  }, [snackbar, makeRequest, runWithDedup]);

  const createCarPlan = useCallback<IActions['createCarPlan']>(
    async (carPlan) => {
      startLoading('createCarPlan');

      try {
        const res = await makeRequest<any>(
          '/api/assortment/plans/car/create',
          undefined,
          'POST',
          carPlan,
        );

        if (res.id) {
          return res;
        }

        throw new Error(
          res?.message || 'An error occurred while creating the car plan.',
        );
      } catch (err: any) {
        snackbar.error(err.message, err);
      } finally {
        stopLoading('createCarPlan');
      }
    },
    [makeRequest, snackbar, startLoading, stopLoading],
  );

  const applyCarPlan = useCallback<IActions['applyCarPlan']>(
    async (planId) => {
      startLoading(`applyCarPlan/${planId}`);

      try {
        if (!planId) {
          throw new Error('Plan ID is required to apply a car plan.');
        }

        const res = await makeRequest<any>(
          '/api/assortment/plans/car/apply',
          undefined,
          'POST',
          { planId },
        );

        if (res) {
          return true;
        }

        throw new Error('An error occurred while applying the car plan.');
      } catch (err: any) {
        snackbar.error(err.message, err);
        return false;
      } finally {
        stopLoading(`applyCarPlan/${planId}`);
      }
    },
    [makeRequest, snackbar, startLoading, stopLoading],
  );

  const getCarPlans = useCallback<IActions['getCarPlans']>(
    async (
      limit,
      offset,
      status,
      modelYear,
      engineType,
      classification,
      search,
    ) => {
      startLoading('getCarPlans');

      try {
        const params = new URLSearchParams({
          limit: String(limit),
          offset: String(offset),
        });

        if (status) {
          params.set('status', status);
        }

        if (!Number.isNaN(modelYear)) {
          params.set('modelYear', String(modelYear));
        }

        if (engineType) {
          params.set('engineType', engineType);
        }

        if (classification) {
          params.set('classification', classification);
        }

        if (search) {
          params.set('search', search);
        }

        const res = await makeRequest<{
          data: IAssortmentCarPlan[];
          metadata: { totalCount: number };
        }>(`/api/assortment/plans/car?${params.toString()}`);

        return res;
      } catch (err: any) {
        snackbar.error(err.message);
      } finally {
        stopLoading('getCarPlans');
      }
    },
    [makeRequest, snackbar, startLoading, stopLoading],
  );

  const getCarPlanFilters = useCallback<
    IActions['getCarPlanFilters']
  >(async () => {
    try {
      return await runWithDedup({
        actionKey: 'getCarPlanFilters',
        request: async () => {
          const filters = await makeRequest<IAssortmentCarPlanFilters>(
            '/api/assortment/plans/car/filters',
          );

          if (!filters || typeof filters !== 'object') {
            throw new Error(
              'An error occurred while fetching car plan filters.',
            );
          }

          return filters;
        },
      });
    } catch (err: any) {
      snackbar.error(err.message);
    }
  }, [makeRequest, runWithDedup, snackbar]);

  // Product and packages

  const createProduct = useCallback<IActions['createProduct']>(
    async (product) => {
      startLoading('createProduct');

      try {
        if (!product.productGroup?.id) {
          throw new Error('Product group is required to create a product.');
        }

        const res = await makeRequest<{
          message?: string;
          data?: IAssortmentPackage;
        }>('/api/assortment/products', undefined, 'POST', product);

        if (res.data) {
          return res.data;
        }

        throw new Error(
          res?.message || 'An error occurred while creating the product.',
        );
      } catch (err: any) {
        snackbar.error(err.message, err);
      } finally {
        stopLoading('createProduct');
      }
    },
    [makeRequest, snackbar, startLoading, stopLoading],
  );

  const updateProduct = useCallback<IActions['updateProduct']>(
    async (id, product) => {
      startLoading(`updateProduct/${id}`);

      try {
        if (!product.productGroup?.id) {
          throw new Error('Product group is required to update a product.');
        }

        const res = await makeRequest<{
          message?: string;
          data?: IAssortmentPackage;
        }>(`/api/assortment/products/${id}`, undefined, 'PUT', product);

        if (res.data) {
          return res.data;
        }

        throw new Error(
          res?.message || 'An error occurred while updating the product.',
        );
      } catch (err: any) {
        snackbar.error(err.message, err);
      } finally {
        stopLoading(`updateProduct/${id}`);
      }
    },
    [makeRequest, snackbar, startLoading, stopLoading],
  );

  const getProducts = useCallback<IActions['getProducts']>(
    async (pgCodes = []) => {
      try {
        const cachedProducts = stateRef.current.products;

        if (cachedProducts.length) {
          return cachedProducts;
        }

        return await runWithDedup({
          actionKey: 'getProducts',
          request: async () => {
            const products = await makeRequest<IAssortmentProduct[]>(
              '/api/assortment/products',
              pgCodes.length ? { pgCodes: pgCodes.join(',') } : undefined,
            );

            if (!Array.isArray(products)) {
              throw new Error('An error occurred while fetching products.');
            }

            setState((prev) => ({
              ...prev,
              products,
            }));

            return products;
          },
        });
      } catch (err: any) {
        snackbar.error(err.message);
        return [];
      }
    },
    [makeRequest, runWithDedup, snackbar],
  );

  const getProductById = useCallback<IActions['getProductById']>(
    async (id, notify = true) => {
      const actionKey = `getProductById/${id}`;

      try {
        return await runWithDedup({
          actionKey,
          request: async () => {
            const product = await makeRequest<IAssortmentProduct>(
              `/api/assortment/products/${id}`,
            );

            if (!product.id) {
              throw new Error('An error occurred while fetching the product.');
            }

            return product;
          },
        });
      } catch (err: any) {
        if (notify) {
          snackbar.error(err.message);
        }
      }
    },
    [makeRequest, runWithDedup, snackbar],
  );

  // Product variants

  const getProductVariants = useCallback<
    IActions['getProductVariants']
  >(async () => {
    try {
      const cachedProductVariants = stateRef.current.productVariants;

      if (cachedProductVariants.length) {
        return cachedProductVariants;
      }

      return await runWithDedup({
        actionKey: 'getProductVariants',
        request: async () => {
          const res = await makeRequest<{
            data?: IAssortmentProductVariant[];
          }>('/api/assortment/products/variants');

          if (!Array.isArray(res.data)) {
            throw new Error(
              'An error occurred while fetching product variants.',
            );
          }

          const productVariants = res.data ?? [];

          setState((prev) => ({
            ...prev,
            productVariants,
          }));

          return productVariants;
        },
      });
    } catch (err: any) {
      snackbar.error(err.message);
      return [];
    }
  }, [snackbar, makeRequest, runWithDedup]);

  // Product groups

  const getProductGroups = useCallback<
    IActions['getProductGroups']
  >(async () => {
    try {
      const cachedProductGroups = stateRef.current.productGroups;

      if (cachedProductGroups.length) {
        return cachedProductGroups;
      }

      return await runWithDedup({
        actionKey: 'getProductGroups',
        request: async () => {
          const productGroups = await makeRequest<IAssortmentProductGroup[]>(
            '/api/assortment/product-groups',
          );

          if (!Array.isArray(productGroups)) {
            throw new Error('An error occurred while fetching product groups.');
          }

          setState((prev) => ({ ...prev, productGroups }));

          return productGroups;
        },
      });
    } catch (err: any) {
      snackbar.error(err.message);
      return [];
    }
  }, [snackbar, makeRequest, runWithDedup]);

  const createProductGroup = useCallback<IActions['createProductGroup']>(
    async (body) => {
      startLoading('createProductGroup');
      try {
        const res = await makeRequest<IAssortmentProductGroup>(
          '/api/assortment/product-groups',
          undefined,
          'POST',
          body,
        );

        if (!res?.id) {
          throw new Error(
            (res as any)?.message ??
              'An error occurred while creating the product group.',
          );
        }

        setState((prev) => ({
          ...prev,
          productGroups: [...prev.productGroups, res].sort((a, b) =>
            (a.name || '').localeCompare(b.name || ''),
          ),
        }));

        return res;
      } catch (err: any) {
        snackbar.error(err.message, err);
      } finally {
        stopLoading('createProductGroup');
      }
    },
    [makeRequest, snackbar, startLoading, stopLoading],
  );

  const updateProductGroup = useCallback<IActions['updateProductGroup']>(
    async (id, body) => {
      startLoading(`updateProductGroup/${id}`);
      try {
        const res = await makeRequest<IAssortmentProductGroup>(
          `/api/assortment/product-groups/${id}`,
          undefined,
          'PUT',
          body,
        );

        if (!res?.id) {
          throw new Error(
            (res as any)?.message ??
              'An error occurred while updating the product group.',
          );
        }

        setState((prev) => ({
          ...prev,
          productGroups: prev.productGroups.map((pg) =>
            pg.id === res.id ? res : pg,
          ),
        }));

        return res;
      } catch (err: any) {
        snackbar.error(err.message, err);
      } finally {
        stopLoading(`updateProductGroup/${id}`);
      }
    },
    [makeRequest, snackbar, startLoading, stopLoading],
  );

  const deleteProductGroup = useCallback<IActions['deleteProductGroup']>(
    async (id) => {
      startLoading(`deleteProductGroup/${id}`);
      try {
        await makeRequest(
          `/api/assortment/product-groups/${id}`,
          undefined,
          'DELETE',
        );

        setState((prev) => ({
          ...prev,
          productGroups: prev.productGroups.filter((pg) => pg.id !== id),
        }));
      } catch (err: any) {
        snackbar.error(err.message, err);
      } finally {
        stopLoading(`deleteProductGroup/${id}`);
      }
    },
    [makeRequest, snackbar, startLoading, stopLoading],
  );

  // Specifications

  const getSpecifications = useCallback<
    IActions['getSpecifications']
  >(async () => {
    try {
      const cachedSpecifications = stateRef.current.specifications;

      if (cachedSpecifications.length) {
        return cachedSpecifications;
      }

      return await runWithDedup({
        actionKey: 'getSpecifications',
        request: async () => {
          const specifications = await makeRequest<IAssortmentSpecification[]>(
            '/api/assortment/specifications',
          );

          if (!Array.isArray(specifications)) {
            throw new Error('An error occurred while fetching specifications.');
          }

          setState((prev) => ({ ...prev, specifications }));

          return specifications;
        },
      });
    } catch (err: any) {
      snackbar.error(err.message);
      return [];
    }
  }, [snackbar, makeRequest, runWithDedup]);

  const createSpecification = useCallback<IActions['createSpecification']>(
    async (body) => {
      startLoading('createSpecification');
      try {
        const res = await makeRequest<IAssortmentSpecification>(
          '/api/assortment/specifications',
          undefined,
          'POST',
          body,
        );

        if (!res?.id) {
          throw new Error(
            (res as any)?.message ??
              'An error occurred while creating the specification.',
          );
        }

        setState((prev) => ({
          ...prev,
          specifications: [...prev.specifications, res].sort((a, b) =>
            (a.name || '').localeCompare(b.name || ''),
          ),
        }));

        return res;
      } catch (err: any) {
        snackbar.error(err.message, err);
      } finally {
        stopLoading('createSpecification');
      }
    },
    [makeRequest, snackbar, startLoading, stopLoading],
  );

  const updateSpecification = useCallback<IActions['updateSpecification']>(
    async (id, body) => {
      startLoading(`updateSpecification/${id}`);
      try {
        const res = await makeRequest<IAssortmentSpecification>(
          `/api/assortment/specifications/${id}`,
          undefined,
          'PUT',
          body,
        );

        if (!res?.id) {
          throw new Error(
            (res as any)?.message ??
              'An error occurred while updating the specification.',
          );
        }

        setState((prev) => ({
          ...prev,
          specifications: prev.specifications.map((s) =>
            s.id === res.id ? res : s,
          ),
        }));

        return res;
      } catch (err: any) {
        snackbar.error(err.message, err);
      } finally {
        stopLoading(`updateSpecification/${id}`);
      }
    },
    [makeRequest, snackbar, startLoading, stopLoading],
  );

  const deleteSpecification = useCallback<IActions['deleteSpecification']>(
    async (id) => {
      startLoading(`deleteSpecification/${id}`);
      try {
        await makeRequest(
          `/api/assortment/specifications/${id}`,
          undefined,
          'DELETE',
        );

        setState((prev) => ({
          ...prev,
          specifications: prev.specifications.filter((s) => s.id !== id),
        }));
      } catch (err: any) {
        snackbar.error(err.message, err);
      } finally {
        stopLoading(`deleteSpecification/${id}`);
      }
    },
    [makeRequest, snackbar, startLoading, stopLoading],
  );

  // Classifications

  const getClassifications = useCallback<
    IActions['getClassifications']
  >(async () => {
    try {
      const cachedClassifications = stateRef.current.classifications;

      if (cachedClassifications.length) {
        return cachedClassifications;
      }

      return await runWithDedup({
        actionKey: 'getClassifications',
        request: async () => {
          const classifications = await makeRequest<
            IAssortmentClassification[]
          >('/api/assortment/classifications');

          if (!Array.isArray(classifications)) {
            throw new Error(
              'An error occurred while fetching classifications.',
            );
          }

          setState((prev) => ({ ...prev, classifications }));

          return classifications;
        },
      });
    } catch (err: any) {
      snackbar.error(err.message);
      return [];
    }
  }, [snackbar, makeRequest, runWithDedup]);

  const createClassification = useCallback<IActions['createClassification']>(
    async (body) => {
      startLoading('createClassification');
      try {
        const res = await makeRequest<IAssortmentClassification>(
          '/api/assortment/classifications',
          undefined,
          'POST',
          body,
        );

        if (!res?.id) {
          throw new Error(
            'An error occurred while creating the classification.',
          );
        }

        setState((prev) => ({
          ...prev,
          classifications: [...prev.classifications, res].sort((a, b) =>
            (a.code || '').localeCompare(b.code || ''),
          ),
        }));

        return res;
      } catch (err: any) {
        snackbar.error(err.message, err);
      } finally {
        stopLoading('createClassification');
      }
    },
    [makeRequest, snackbar, startLoading, stopLoading],
  );

  const updateClassification = useCallback<IActions['updateClassification']>(
    async (id, body) => {
      startLoading(`updateClassification/${id}`);
      try {
        const res = await makeRequest<IAssortmentClassification>(
          `/api/assortment/classifications/${id}`,
          undefined,
          'PUT',
          body,
        );

        if (!res?.id) {
          throw new Error(
            'An error occurred while updating the classification.',
          );
        }

        setState((prev) => ({
          ...prev,
          classifications: prev.classifications.map((c) =>
            c.id === res.id ? res : c,
          ),
        }));

        return res;
      } catch (err: any) {
        snackbar.error(err.message, err);
      } finally {
        stopLoading(`updateClassification/${id}`);
      }
    },
    [makeRequest, snackbar, startLoading, stopLoading],
  );

  const deleteClassification = useCallback<IActions['deleteClassification']>(
    async (id) => {
      startLoading(`deleteClassification/${id}`);
      try {
        await makeRequest(
          `/api/assortment/classifications/${id}`,
          undefined,
          'DELETE',
        );

        setState((prev) => ({
          ...prev,
          classifications: prev.classifications.filter((c) => c.id !== id),
        }));
      } catch (err: any) {
        snackbar.error(err.message, err);
      } finally {
        stopLoading(`deleteClassification/${id}`);
      }
    },
    [makeRequest, snackbar, startLoading, stopLoading],
  );

  // SKU Categories

  const getSkuCategories = useCallback<
    IActions['getSkuCategories']
  >(async () => {
    // TODO: Use this only when M&S SKU categories are migrated to Assortment
    try {
      const cachedSkuCategories = stateRef.current.skuCategories;

      if (cachedSkuCategories.length) {
        return cachedSkuCategories;
      }

      return await runWithDedup({
        actionKey: 'getSkuCategories',
        request: async () => {
          const skuCategories = await makeRequest<IAssortmentSkuCategory[]>(
            '/api/assortment/sku-categories',
          );

          if (!Array.isArray(skuCategories)) {
            throw new Error('An error occurred while fetching SKU categories.');
          }

          setState((prev) => ({ ...prev, skuCategories }));

          return skuCategories;
        },
      });
    } catch (err: any) {
      snackbar.error(err.message);
      return [];
    }
  }, [snackbar, makeRequest, runWithDedup]);

  // Actions memo

  const actions = useMemo<IActions>(
    () => ({
      getCarPlans,
      getProducts,
      applyCarPlan,
      createCarPlan,
      createProduct,
      updateProduct,
      getCarProducts,
      getProductById,
      getAvailableCars,
      getProductGroups,
      getSkuCategories,
      getCarPlanFilters,
      getSpecifications,
      createProductGroup,
      getClassifications,
      getProductVariants,
      updateProductGroup,
      deleteProductGroup,
      createSpecification,
      updateSpecification,
      deleteSpecification,
      createClassification,
      updateClassification,
      deleteClassification,
    }),
    [
      getCarPlans,
      getProducts,
      applyCarPlan,
      createProduct,
      updateProduct,
      createCarPlan,
      getProductById,
      getCarProducts,
      getAvailableCars,
      getProductGroups,
      getSkuCategories,
      getCarPlanFilters,
      getSpecifications,
      createProductGroup,
      updateProductGroup,
      deleteProductGroup,
      getProductVariants,
      getClassifications,
      createSpecification,
      updateSpecification,
      deleteSpecification,
      createClassification,
      updateClassification,
      deleteClassification,
    ],
  );

  return [{ ...state, loading: pendingActions.size > 0 }, actions];
};
